import React, { useState } from 'react';
import { useParams, useSearch } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Spinner } from '@/components/ui/spinner';
import { useRebateCalculationAudit } from 'src/api/rebates/useRebateCalculationAudit';
import { formatCurrency } from '@erp/shared-utils';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import {
  WarningCircleIcon,
  FileTextIcon,
  ReceiptIcon,
} from '@phosphor-icons/react';
import { RuleGroupedView } from 'src/components/rebates/audit/RuleGroupedView';
import { InvoiceGroupedView } from 'src/components/rebates/audit/InvoiceGroupedView';

export function RebateCalculationAuditPage() {
  const { id: agreementId } = useParams({ strict: false });
  const search = useSearch({ strict: false }) as {
    date?: string;
    calculationType?: 'estimate' | 'prediction';
  };

  const [activeView, setActiveView] = useState<'by-rule' | 'by-invoice'>(
    'by-rule',
  );

  // Parse date if provided
  const targetDate = search.date ? new Date(search.date) : undefined;
  const calculationType = search.calculationType || 'estimate';

  // Fetch audit data
  const {
    data: audit,
    isLoading,
    error,
  } = useRebateCalculationAudit({
    agreementId,
    date: targetDate,
    calculationType,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">
            Generating calculation audit...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <AlertContainer variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertDescription>
            Failed to load calculation audit: {error.message}
          </AlertDescription>
        </AlertContainer>
      </div>
    );
  }

  if (!audit) {
    return (
      <div className="container mx-auto py-6">
        <AlertContainer>
          <WarningCircleIcon className="h-4 w-4" />
          <AlertDescription>No audit data available</AlertDescription>
        </AlertContainer>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">{audit.agreementName}</h1>
        <p className="text-muted-foreground mt-1">
          Calculation Audit - {audit.supplierName}
        </p>
        <p className="text-sm text-muted-foreground">
          {new Date(audit.targetDate).toLocaleDateString()} •{' '}
          {calculationType === 'estimate'
            ? 'YTD Expected'
            : 'End of Term Prediction'}
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Rebate Amount
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(audit.totalRebateAmount)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Automatic Rules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(audit.automaticRebateAmount)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Manual Adjustments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(audit.manualRebateAmount)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dual View Tabs */}
      <Card>
        <CardHeader>
          <Tabs
            value={activeView}
            onValueChange={(v) => setActiveView(v as 'by-rule' | 'by-invoice')}
          >
            <TabsList className="grid w-full max-w-[400px] grid-cols-2">
              <TabsTrigger value="by-rule" className="flex items-center gap-2">
                <FileTextIcon className="h-4 w-4" />
                By Rule
              </TabsTrigger>
              <TabsTrigger
                value="by-invoice"
                className="flex items-center gap-2"
              >
                <ReceiptIcon className="h-4 w-4" />
                By Invoice
              </TabsTrigger>
            </TabsList>

            <TabsContent value="by-rule" className="mt-6">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Shows how each rule performed, with tier progress and
                  contributing invoices
                </p>
                <RuleGroupedView rules={audit.ruleAudits} />
              </div>
            </TabsContent>

            <TabsContent value="by-invoice" className="mt-6">
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Shows how each invoice was evaluated against all rules
                </p>
                <InvoiceGroupedView invoices={audit.invoiceAudits} />
              </div>
            </TabsContent>
          </Tabs>
        </CardHeader>
      </Card>

      {/* Debug info in development */}
      {process.env.NODE_ENV === 'development' && (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle className="text-sm">Debug Info</CardTitle>
          </CardHeader>
          <CardContent className="text-xs">
            <pre className="overflow-auto">
              {JSON.stringify(
                {
                  ruleCount: audit.ruleAudits.length,
                  invoiceCount: audit.invoiceAudits.length,
                  dateRange: audit.metadata.dateRange,
                },
                null,
                2,
              )}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default RebateCalculationAuditPage;
